import {
  getPhaseBoundaryKeys,
  PHASE_BOUNDARY_KEYS,
  phaseBoundaryDeadlineLabel,
} from "../../../constants/project-dates/projectDateFieldKeys";
import { ProjectPhase } from "../../../constants/project-phase";
import { ProjectDatesSchema, SelectData } from "../../../models";
import { camelCaseToSentenceCase, sentenceOrPascalToCamel } from "../../../utils";

const PHASE_DATE_SCHEMA_KEY_MAP: Record<ProjectPhase, keyof ProjectDatesSchema> = {
  [ProjectPhase.feasibility]: "feasibility",
  [ProjectPhase.design]: "design",
  [ProjectPhase.implementation]: "implementation",
  [ProjectPhase.ongoingMonitoring]: "ongoingMonitoring",
};

// `key` is what gets stored in workflow_template_task.default_deadline_field and `value` is only
// the label. The stored form is a camelCase key inside the step phase's object in the target's
// dates JSON - the same rule for project and deal templates.
//
// Every phase object carries its own boundary dates, so those are offered for any phase rather than
// only the four project ones; which pair a phase spells them with comes from the shared map.
const phaseBoundarySelectData = (phase: string, isProjectTemplate: boolean): SelectData => {
  const [start, end] = getPhaseBoundaryKeys(phase, isProjectTemplate);
  return [
    { key: start, value: phaseBoundaryDeadlineLabel(phase, start) },
    { key: end, value: phaseBoundaryDeadlineLabel(phase, end) },
  ];
};

// Anchors to the project row rather than to `dates`, and only valid on the opening phase.
const CREATION_DATE_SELECT_DATA: SelectData = [{ key: "creationDate", value: "Creation date" }];

// The boundary and structural keys of a phase object are not named "...Date", so this one test is
// enough to leave them out - they are appended separately from the shared boundary map instead.
const isPhaseKeyDateProperty = (propertyName: string): boolean => propertyName.toLowerCase().endsWith("date");

const getDateSchemas = (dateSchema: ProjectDatesSchema | ProjectDatesSchema[] | undefined): ProjectDatesSchema[] => {
  if (dateSchema === undefined) {
    return [];
  }

  if (Array.isArray(dateSchema)) {
    return dateSchema;
  }

  return [dateSchema];
};

export const getPhaseKeyDatesSelectDataFromDateSchema = (
  phase: string | undefined,
  dateSchema: ProjectDatesSchema | ProjectDatesSchema[] | undefined,
  phaseKeyDates?: string[],
  isProjectTemplate = false
): SelectData => {
  if (!phase) return [];

  // Only a project template's phases are ProjectPhase values. Every other template type names its
  // stages freely, so matching one against this map would hand a deal stage called "design" the
  // project design schema's key dates.
  const phaseSchemaKey = isProjectTemplate ? PHASE_DATE_SCHEMA_KEY_MAP[phase as ProjectPhase] : undefined;
  const schemas = getDateSchemas(dateSchema);
  // A phase that lists one of its own boundaries among its key dates produces the same key as the
  // boundary options appended below, and wins the de-duplication because it comes first. Label it
  // through the shared helper so whichever copy survives reads as the phase-qualified option rather
  // than a bare "Stage start".
  const queryDrivenSelectData =
    phaseKeyDates?.map((keyDate) => ({
      key: keyDate,
      value: PHASE_BOUNDARY_KEYS.has(keyDate)
        ? phaseBoundaryDeadlineLabel(phase, keyDate)
        : camelCaseToSentenceCase(sentenceOrPascalToCamel(keyDate)),
    })) ?? [];

  const schemaDrivenSelectData = schemas.flatMap((schema) => {
    const phaseProperties =
      phaseSchemaKey === undefined
        ? undefined
        : (schema?.[phaseSchemaKey]?.properties as Record<string, unknown> | undefined);
    return Object.keys(phaseProperties ?? {})
      .filter(isPhaseKeyDateProperty)
      .map((propertyName) => {
        // Store the schema's camelCase property name; show it sentence-cased.
        return { key: propertyName, value: camelCaseToSentenceCase(propertyName) };
      });
  });

  const fullSelectData = [
    ...queryDrivenSelectData,
    ...schemaDrivenSelectData,
    ...(phase === ProjectPhase.feasibility ? CREATION_DATE_SELECT_DATA : []),
    ...phaseBoundarySelectData(phase, isProjectTemplate),
  ];
  return fullSelectData.filter(
    (option, index, data) => data.findIndex((candidate) => candidate.key === option.key) === index
  ) as SelectData;
};
